class B2BOrdering extends HTMLElement {
  constructor() {
    super();
    this.apiUrl = this.dataset.apiUrl;
    this.customerEmail = this.dataset.customerEmail;
    this.customerName = this.dataset.customerName;
    this.customerAddress = this.dataset.customerAddress;
    this.currency = this.dataset.currency || '€';

    this.products = [];
    this.filtered = [];
    this.cart = {};
    this.filters = { model: '', category: '', search: '' };
    this.PAGE_SIZE = 50;
    this.renderPage = 0;

    this.modelMap = {
      S5: 'Joyor S5',
      S5A: 'Joyor S5 ABE',
      S5AP: 'Joyor S5 ABE PRO',
      S5Z: 'Joyor S5-Z',
      S8Z: 'Joyor S8-Z',
      S8S: 'Joyor S8-S',
      S8SZ: 'Joyor S8-S-Z',
      S8E: 'Joyor S8E',
      S10S: 'Joyor S10-S',
      S10SZ: 'Joyor S10-S-Z',
      Y5S: 'Joyor Y5-S',
      Y6S: 'Joyor Y6-S',
      Y8S: 'Joyor Y8-S',
      Y10: 'Joyor Y10',
      T6E: 'Joyor T6E',
      T6EP: 'Joyor TE Pro',
      T10: 'Joyor T10',
      T6: 'Joyor T6',
      F5: 'Joyor F5',
      C10: 'Joyor C10',
      C10E: 'Joyor C10-E',
      UNI: 'Universal',
      USD: 'Used',
      T: 'Joyor T',
      Y: 'Joyor Y',
      S: 'Joyor S',
      C: 'Joyor C',
      F: 'Joyor F'
    };

    this.categoryMap = {
      BRK: 'Brake System',
      TIR: 'Tire & Wheel Hub',
      CTL: 'Controller & Dashboard',
      BAT: 'Battery',
      CHG: 'Charger',
      LGT: 'Lighting',
      MTR: 'Motor',
      FDR: 'Fender',
      BDY: 'Body & Frame',
      HDL: 'Handlebar & Stem',
      SEC: 'Security & Lock',
      ST: 'Seat',
      HW: 'Hardware',
      PNT: 'Paint & Care',
      ACC: 'Accessories',
      SRV: 'Service Fee',
      USD: 'Used Parts',
      BUT: 'Button & Switch',
      ABS: 'Absorber / Suspension',
      TOL: 'Tool'
    };

    this.tableBody = this.querySelector('[data-table-body]');
    this.countEl = this.querySelector('[data-product-count]');
    this.countWrap = this.querySelector('.b2b-count');
    this.loadingEl = this.querySelector('[data-loading]');
    this.cartBar = this.querySelector('[data-cart-bar]');
    this.cartCountEl = this.querySelector('[data-cart-count]');
    this.cartTotalEl = this.querySelector('[data-cart-total]');
    this.cartDetailList = this.querySelector('[data-cart-detail-list]');
    this.cartDetail = this.querySelector('[data-cart-detail]');
    this.modalOverlay = this.querySelector('[data-modal-overlay]');

    this.init();
  }

  async init() {
    await this.fetchProducts();
    this.buildFilterOptions();
    this.bindEvents();
    this.render();
  }

  /* ── Data fetching ── */

  async fetchProducts() {
    if (!this.apiUrl) return;
    try {
      // Use script injection to bypass CORS (Google Apps Script redirect issue)
      const data = await this.fetchViaScript(this.apiUrl);
      if (data.success) {
        this.products = data.products.map(p => ({
          ...p,
          model: this.modelMap[p.model] || p.model,
          category: this.categoryMap[p.category] || p.category
        }));
        this.filtered = [...this.products];
      }
    } catch (e) {
      console.error('B2B: fetch failed', e);
    }
    if (this.loadingEl) this.loadingEl.style.display = 'none';
    if (this.countWrap) this.countWrap.style.display = 'block';
  }

  /* ── Filter options ── */

  buildFilterOptions() {
    const models = [...new Set(this.products.map(p => p.model).filter(Boolean))].sort();
    const categories = [...new Set(this.products.map(p => p.category).filter(Boolean))].sort();

    this.populateDropdown(this.querySelector('[data-dropdown="model"]'), models);
    this.populateDropdown(this.querySelector('[data-dropdown="category"]'), categories);
  }

  populateDropdown(container, items) {
    if (!container) return;
    // Keep the "All" button, add rest
    items.forEach(item => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'b2b-filters__option';
      btn.dataset.value = item;
      btn.textContent = item;
      container.appendChild(btn);
    });
  }

  /* ── Events ── */

  bindEvents() {
    // Search
    const searchInput = this.querySelector('[data-search]');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this.filters.search = e.target.value.toLowerCase();
        this.applyFilters();
      });
    }

    // Filter dropdowns
    this.querySelectorAll('.b2b-filters__group').forEach(group => {
      const btn = group.querySelector('.b2b-filters__dropdown-btn');
      const dropdown = group.querySelector('.b2b-filters__dropdown');
      const filterKey = group.dataset.filterKey;

      btn.addEventListener('click', () => {
        const isOpen = dropdown.classList.contains('is-open');
        this.closeAllDropdowns();
        if (!isOpen) {
          dropdown.classList.add('is-open');
          btn.classList.add('is-open');
        }
      });

      dropdown.addEventListener('click', (e) => {
        const option = e.target.closest('.b2b-filters__option');
        if (!option) return;

        dropdown.querySelectorAll('.b2b-filters__option').forEach(o => o.classList.remove('is-active'));
        option.classList.add('is-active');

        const value = option.dataset.value;
        this.filters[filterKey] = value;

        const label = btn.querySelector('span');
        if (value) {
          label.textContent = value;
          btn.classList.add('has-value');
        } else {
          label.textContent = btn.dataset.defaultLabel;
          btn.classList.remove('has-value');
        }

        this.closeAllDropdowns();
        this.applyFilters();
      });
    });

    // Close dropdowns on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.b2b-filters__group')) {
        this.closeAllDropdowns();
      }
    });

    // Image lightbox (delegation)
    if (this.tableBody) {
      this.tableBody.addEventListener('click', (e) => {
        const imageWrap = e.target.closest('.b2b-table__image-wrap');
        if (!imageWrap) return;
        e.preventDefault();
        e.stopPropagation();
        const row = imageWrap.closest('tr');
        if (!row) { console.log('B2B lightbox: no row found'); return; }
        const sku = row.dataset.sku;
        const product = this.products.find(p => p.sku === sku);
        if (!product) { console.log('B2B lightbox: no product for sku', sku); return; }
        const images = (product.image || '').split(',').map(u => this.convertGitHubUrl(u.trim())).filter(Boolean);
        console.log('B2B lightbox: opening with', images.length, 'images');
        if (images.length > 0) this.openLightbox(images);
      });
    }

    // Table quantity events (delegation)
    if (this.tableBody) {
      this.tableBody.addEventListener('click', (e) => {
        const btn = e.target.closest('.b2b-table__qty-btn');
        if (!btn) return;
        const row = btn.closest('tr');
        const sku = row.dataset.sku;
        const input = row.querySelector('.b2b-table__qty-input');
        const product = this.products.find(p => p.sku === sku);
        const minQty = product ? product.minQty : 1;
        let val = parseInt(input.value) || 0;

        if (btn.dataset.action === 'minus') {
          val = val <= minQty ? 0 : val - 1;
        } else {
          val = val < minQty ? minQty : val + 1;
        }

        input.value = val || '';
        this.updateCart(sku, val);
      });

      this.tableBody.addEventListener('change', (e) => {
        const input = e.target.closest('.b2b-table__qty-input');
        if (!input) return;
        const row = input.closest('tr');
        const sku = row.dataset.sku;
        const product = this.products.find(p => p.sku === sku);
        const minQty = product ? product.minQty : 1;
        let val = parseInt(input.value) || 0;
        if (val > 0 && val < minQty) val = minQty;
        input.value = val || '';
        this.updateCart(sku, val);
      });
    }

    // Cart bar expand
    const expandBtn = this.querySelector('[data-cart-expand]');
    if (expandBtn) {
      expandBtn.addEventListener('click', () => {
        this.cartDetail.classList.toggle('is-open');
      });
    }

    // Cart detail remove
    if (this.cartDetailList) {
      this.cartDetailList.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-remove-sku]');
        if (!removeBtn) return;
        const sku = removeBtn.dataset.removeSku;
        this.updateCart(sku, 0);
        // Also clear table input
        const tableRow = this.tableBody.querySelector(`tr[data-sku="${sku}"]`);
        if (tableRow) {
          tableRow.querySelector('.b2b-table__qty-input').value = '';
        }
      });
    }

    // Submit order button
    const submitBtn = this.querySelector('[data-cart-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', () => this.openOrderModal());
    }
  }

  closeAllDropdowns() {
    this.querySelectorAll('.b2b-filters__dropdown').forEach(d => d.classList.remove('is-open'));
    this.querySelectorAll('.b2b-filters__dropdown-btn').forEach(b => b.classList.remove('is-open'));
  }

  /* ── Filtering ── */

  applyFilters() {
    this.filtered = this.products.filter(p => {
      if (this.filters.model && p.model !== this.filters.model) return false;
      if (this.filters.category && p.category !== this.filters.category) return false;
      if (this.filters.search) {
        const q = this.filters.search;
        const haystack = (p.name + ' ' + p.sku + ' ' + p.model + ' ' + p.category).toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
    this.renderPage = 0;
    this.render();
  }

  /* ── Render table (paginated) ── */

  renderRow(p) {
    const allImages = (p.image || '').split(',').map(u => this.convertGitHubUrl(u.trim())).filter(Boolean);
    const imgUrl = allImages[0] || '';
    const inCart = this.cart[p.sku] > 0;
    const qty = this.cart[p.sku] || '';
    return `<tr data-sku="${this.escapeHtml(p.sku)}" class="${inCart ? 'b2b-table__row--in-cart' : ''}">
      <td>
        <div class="b2b-table__product-info">
          ${imgUrl ? `<div class="b2b-table__image-wrap">
            <img class="b2b-table__image" src="${this.escapeHtml(imgUrl)}" alt="" loading="lazy">
            ${allImages.length > 1 ? `<span class="b2b-table__image-count">${allImages.length}</span>` : ''}
          </div>` : ''}
          <div>
            <div class="b2b-table__name">${this.escapeHtml(p.name)}</div>
            <div class="b2b-table__sku">${this.escapeHtml(p.sku)}</div>
          </div>
        </div>
      </td>
      <td class="b2b-table__model">${this.escapeHtml(p.model)}</td>
      <td class="b2b-table__category">${this.escapeHtml(p.category)}</td>
      <td class="b2b-table__price">${this.currency}${p.price.toFixed(2)}</td>
      <td>
        <div class="b2b-table__qty">
          <button type="button" class="b2b-table__qty-btn" data-action="minus">&minus;</button>
          <input type="number" class="b2b-table__qty-input" value="${qty}" min="0" placeholder="-">
          <button type="button" class="b2b-table__qty-btn" data-action="plus">&plus;</button>
        </div>
      </td>
    </tr>`;
  }

  render() {
    if (!this.tableBody) return;
    this.renderPage = 0;

    if (this.countEl) {
      this.countEl.textContent = this.filtered.length + ' products';
    }

    if (this.filtered.length === 0) {
      this.tableBody.innerHTML = '<tr><td colspan="6" class="b2b-table__empty">No products found</td></tr>';
      this.removeLoadMore();
      return;
    }

    const batch = this.filtered.slice(0, this.PAGE_SIZE);
    this.tableBody.innerHTML = batch.map(p => this.renderRow(p)).join('');
    this.renderPage = 1;
    this.updateLoadMore();
  }

  loadMore() {
    const start = this.renderPage * this.PAGE_SIZE;
    const batch = this.filtered.slice(start, start + this.PAGE_SIZE);
    if (batch.length === 0) return;
    this.tableBody.insertAdjacentHTML('beforeend', batch.map(p => this.renderRow(p)).join(''));
    this.renderPage++;
    this.updateLoadMore();
  }

  updateLoadMore() {
    const shown = Math.min(this.renderPage * this.PAGE_SIZE, this.filtered.length);
    const remaining = this.filtered.length - shown;

    if (remaining <= 0) {
      this.removeLoadMore();
      return;
    }

    if (!this._loadMoreRow) {
      this._loadMoreRow = document.createElement('tr');
      this._loadMoreRow.className = 'b2b-table__load-more-row';
      this._loadMoreRow.innerHTML = '<td colspan="5"><button type="button" class="b2b-table__load-more-btn"></button></td>';
      this._loadMoreRow.querySelector('button').addEventListener('click', () => this.loadMore());
    }
    this._loadMoreRow.querySelector('button').textContent = 'Load more (' + remaining + ' remaining)';

    // Append or re-append
    this.tableBody.appendChild(this._loadMoreRow);

    // Auto-load with IntersectionObserver
    if (!this._loadMoreObserver) {
      this._loadMoreObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) this.loadMore();
      }, { rootMargin: '200px' });
    }
    this._loadMoreObserver.observe(this._loadMoreRow);
  }

  removeLoadMore() {
    if (this._loadMoreRow && this._loadMoreRow.parentNode) {
      if (this._loadMoreObserver) this._loadMoreObserver.unobserve(this._loadMoreRow);
      this._loadMoreRow.remove();
    }
  }

  /* ── Cart ── */

  updateCart(sku, qty) {
    if (qty > 0) {
      this.cart[sku] = qty;
    } else {
      delete this.cart[sku];
    }
    this.renderCartBar();

    // Highlight row
    const row = this.tableBody.querySelector(`tr[data-sku="${sku}"]`);
    if (row) {
      row.classList.toggle('b2b-table__row--in-cart', qty > 0);
    }
  }

  renderCartBar() {
    const entries = Object.entries(this.cart);
    const itemCount = entries.reduce((sum, [, qty]) => sum + qty, 0);
    const total = entries.reduce((sum, [sku, qty]) => {
      const p = this.products.find(p => p.sku === sku);
      return sum + (p ? p.price * qty : 0);
    }, 0);

    if (this.cartCountEl) this.cartCountEl.textContent = itemCount;
    if (this.cartTotalEl) this.cartTotalEl.textContent = this.currency + total.toFixed(2);

    if (this.cartBar) {
      this.cartBar.classList.toggle('is-visible', entries.length > 0);
    }

    // Render detail list
    if (this.cartDetailList) {
      if (entries.length === 0) {
        this.cartDetailList.innerHTML = '';
        this.cartDetail.classList.remove('is-open');
        return;
      }
      this.cartDetailList.innerHTML = entries.map(([sku, qty]) => {
        const p = this.products.find(p => p.sku === sku);
        if (!p) return '';
        return `<li class="b2b-cart-detail__item">
          <div class="b2b-cart-detail__item-info">
            <span class="b2b-cart-detail__item-name">${this.escapeHtml(p.name)}</span>
            <span class="b2b-cart-detail__item-qty">x${qty}</span>
          </div>
          <span class="b2b-cart-detail__item-price">${this.currency}${(p.price * qty).toFixed(2)}</span>
          <button type="button" class="b2b-cart-detail__item-remove" data-remove-sku="${this.escapeHtml(sku)}">&times;</button>
        </li>`;
      }).join('');
    }
  }

  /* ── Order modal ── */

  openOrderModal() {
    if (!this.modalOverlay) return;
    const entries = Object.entries(this.cart);
    if (entries.length === 0) return;

    // Pre-fill address
    const addressDefault = this.modalOverlay.querySelector('[data-address-default]');
    if (addressDefault) addressDefault.textContent = this.customerAddress || 'No saved address';

    this.modalOverlay.classList.add('is-open');

    // Bind modal events (once)
    if (!this._modalBound) {
      this._modalBound = true;

      this.modalOverlay.querySelector('[data-modal-cancel]').addEventListener('click', () => {
        this.modalOverlay.classList.remove('is-open');
      });

      this.modalOverlay.addEventListener('click', (e) => {
        if (e.target === this.modalOverlay) this.modalOverlay.classList.remove('is-open');
      });

      this.modalOverlay.querySelector('[data-modal-confirm]').addEventListener('click', () => {
        this.submitOrder();
      });

      // Address card toggle
      this.modalOverlay.querySelectorAll('input[name="address_type"]').forEach(radio => {
        radio.addEventListener('change', () => {
          const customField = this.modalOverlay.querySelector('[data-custom-address-field]');
          customField.style.display = radio.value === 'custom' ? 'block' : 'none';
          this.modalOverlay.querySelectorAll('.b2b-modal__address-card').forEach(card => {
            card.classList.toggle('is-selected', card.querySelector('input').checked);
          });
        });
      });
    }
  }

  async submitOrder() {
    const confirmBtn = this.modalOverlay.querySelector('[data-modal-confirm]');
    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Submitting...';

    const addressType = this.modalOverlay.querySelector('input[name="address_type"]:checked').value;
    let address = this.customerAddress || '';
    if (addressType === 'custom') {
      address = this.modalOverlay.querySelector('[data-custom-address-input]').value;
    }
    const note = this.modalOverlay.querySelector('[data-order-note]').value;

    const items = Object.entries(this.cart).map(([sku, qty]) => {
      const p = this.products.find(p => p.sku === sku);
      return { sku, name: p ? p.name : '', qty };
    });

    try {
      const result = await this.postData(this.apiUrl, {
        customerEmail: this.customerEmail,
        customerName: this.customerName,
        items,
        address,
        note
      });

      if (result.success) {
        this.showSuccess(result.orderId, result.total);
        this.cart = {};
        this.renderCartBar();
        this.render();
      } else {
        alert('Order failed: ' + (result.error || 'Unknown error'));
      }
    } catch (e) {
      alert('Network error: ' + e.message);
    }

    confirmBtn.disabled = false;
    confirmBtn.textContent = 'Confirm Order';
  }

  showSuccess(orderId, total) {
    const modal = this.modalOverlay.querySelector('.b2b-modal');
    modal.innerHTML = `
      <div class="b2b-success">
        <div class="b2b-success__icon">&#10003;</div>
        <h3>Order Submitted!</h3>
        <div class="b2b-success__order-id">${this.escapeHtml(orderId)}</div>
        <p>Total: ${this.currency}${this.escapeHtml(total)}</p>
        <p>A confirmation email has been sent to ${this.escapeHtml(this.customerEmail)}.</p>
        <p>We will review your order and send an invoice shortly.</p>
        <div class="b2b-modal__btns" style="margin-top:2rem">
          <button class="b2b-modal__btn-confirm" onclick="this.closest('.b2b-modal-overlay').classList.remove('is-open')">Close</button>
        </div>
      </div>`;
  }

  /* ── Lightbox ── */

  openLightbox(images) {
    if (window.B2BLightbox) {
      window.B2BLightbox.open(images);
    }
  }


  /* ── Helpers ── */

  // Bypass CORS by using JSONP callback for GET requests
  fetchViaScript(url) {
    return new Promise((resolve, reject) => {
      const callbackName = '_b2bCallback_' + Date.now();
      const script = document.createElement('script');

      window[callbackName] = (data) => {
        delete window[callbackName];
        script.remove();
        resolve(data);
      };

      script.src = url + (url.includes('?') ? '&' : '?') + 'callback=' + callbackName;
      script.onerror = () => {
        delete window[callbackName];
        script.remove();
        reject(new Error('Script load failed'));
      };
      document.head.appendChild(script);
    });
  }

  // POST via form submission to avoid CORS preflight
  async postData(url, payload) {
    return new Promise((resolve, reject) => {
      const callbackName = '_b2bPostCallback_' + Date.now();

      window[callbackName] = (data) => {
        delete window[callbackName];
        resolve(data);
      };

      // Create a hidden form + iframe approach, or use script with POST data in URL
      // For Apps Script, we can pass POST data as a URL parameter and use doGet to handle it
      const script = document.createElement('script');
      const encodedPayload = encodeURIComponent(JSON.stringify(payload));
      script.src = url + '?callback=' + callbackName + '&action=order&payload=' + encodedPayload;
      script.onerror = () => {
        delete window[callbackName];
        script.remove();
        reject(new Error('Post failed'));
      };
      document.head.appendChild(script);
    });
  }

  convertGitHubUrl(url) {
    if (!url) return '';
    return url.replace(
      /github\.com\/([^/]+)\/([^/]+)\/blob\/([^/]+)\//,
      'raw.githubusercontent.com/$1/$2/$3/'
    );
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

customElements.define('b2b-ordering', B2BOrdering);
